import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'

import { get, put, del, post } from '@/lib/api'

// ==================== Types ====================

export type NotificationType =
  | 'approval_request'
  | 'approval_result'
  | 'task_assigned'
  | 'task_mentioned'
  | 'system_announcement'

export type NotificationChannel = 'in_app' | 'email'

export type RelatedType = 'leave' | 'reimbursement' | 'issue' | 'comment' | 'announcement'

export interface Notification {
  id: string
  type: NotificationType
  title: string
  content: string
  recipientId: string
  relatedType: RelatedType | null
  relatedId: string | null
  isRead: boolean
  createdAt: string
  updatedAt: string | null
}

export interface NotificationPreference {
  notificationType: NotificationType
  enabled: boolean
  channels: NotificationChannel[]
}

export interface NotificationFilters {
  type?: NotificationType
  isRead?: boolean
  startDate?: string
  endDate?: string
  page?: number
  pageSize?: number
}

export interface UpdatePreferenceInput {
  enabled: boolean
  channels: NotificationChannel[]
}

// ==================== Query Keys ====================

export const notificationKeys = {
  all: ['notifications'] as const,
  lists: () => [...notificationKeys.all, 'list'] as const,
  list: (params: NotificationFilters) => [...notificationKeys.lists(), params] as const,
  unreadCount: () => [...notificationKeys.all, 'unread-count'] as const,
  preferences: () => [...notificationKeys.all, 'preferences'] as const,
}

// ==================== Hooks ====================

/**
 * 获取通知列表
 */
export function useNotifications(params: NotificationFilters = {}) {
  return useQuery({
    queryKey: notificationKeys.list(params),
    queryFn: () =>
      get<{
        data: Notification[]
        total: number
        page: number
        pageSize: number
        totalPages: number
      }>('/notifications', params as Record<string, unknown>),
  })
}

/**
 * 获取未读通知数量
 * 配置 30 秒轮询和窗口聚焦刷新
 */
export function useUnreadCount() {
  return useQuery({
    queryKey: notificationKeys.unreadCount(),
    queryFn: () => get<{ count: number }>('/notifications/unread-count'),
    refetchInterval: 30000, // 30 秒轮询
    refetchOnWindowFocus: true, // 窗口聚焦时刷新
  })
}

/**
 * 标记单条通知为已读
 */
export function useMarkAsRead() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (id: string) => put<null>(`/notifications/${id}/read`),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: notificationKeys.lists() })
      void queryClient.invalidateQueries({ queryKey: notificationKeys.unreadCount() })
    },
  })
}

/**
 * 标记所有通知为已读
 */
export function useMarkAllAsRead() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: () => put<null>('/notifications/read-all'),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: notificationKeys.lists() })
      void queryClient.invalidateQueries({ queryKey: notificationKeys.unreadCount() })
    },
  })
}

/**
 * 删除通知
 */
export function useDeleteNotification() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (id: string) => del<null>(`/notifications/${id}`),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: notificationKeys.lists() })
      void queryClient.invalidateQueries({ queryKey: notificationKeys.unreadCount() })
    },
  })
}

/**
 * 批量标记通知为已读
 */
export function useBatchMarkAsRead() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (ids: string[]) => put<null>('/notifications/batch-read', { ids }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: notificationKeys.lists() })
      void queryClient.invalidateQueries({ queryKey: notificationKeys.unreadCount() })
    },
  })
}

/**
 * 批量删除通知
 */
export function useBatchDeleteNotifications() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (ids: string[]) => post<null>('/notifications/batch-delete', { ids }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: notificationKeys.lists() })
      void queryClient.invalidateQueries({ queryKey: notificationKeys.unreadCount() })
    },
  })
}

/**
 * 获取通知偏好设置
 */
export function useNotificationPreferences() {
  return useQuery({
    queryKey: notificationKeys.preferences(),
    queryFn: () => get<NotificationPreference[]>('/notifications/preferences'),
  })
}

/**
 * 更新通知偏好设置
 */
export function useUpdatePreference() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({ type, data }: { type: NotificationType; data: UpdatePreferenceInput }) =>
      put<null>(`/notifications/preferences/${type}`, data),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: notificationKeys.preferences() })
    },
  })
}
